import { TagDetailsPage } from "@/pages/procesos/tag-details";
import RoleGuard from "@/pages/role-guard";
import { createFlowDetailsBreadcrumb } from "@/queries/flows/flows.api";
import { createProcessDetailsBreadcrumb } from "@/queries/process/process.api";
import { Permissions } from "@/utils/permissions";
import type { QueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { lazy, type ReactNode } from "react";
import { redirect } from "react-router";
import {
  makeHandle,
  withNotFoundRedirect,
  type LoaderParams,
} from "./router.helpers";

const ProcessPage = lazy(() => import("@/pages/procesos"));

const EditFlowPage = lazy(() => import("@/pages/flows/edit"));

const FlowExecution = lazy(() => import("@/pages/flows/execution"));

const DetalleProceso = lazy(() =>
  import("@/pages/procesos/details").then((m) => ({
    default: m.DetalleProceso,
  })),
);

/** Packs the process and flow names into a single loader value. */
const FLOW_BREADCRUMB_SEPARATOR = "-seperator-breadcrum-#321309-";

const createProcessFlowLoader =
  (queryClient: QueryClient, hasPermission: (permission: string) => boolean) =>
  async ({ params }: LoaderParams) => {
    let process;
    try {
      if (!params.id) {
        return redirect("/procesos");
      }
      process = await createProcessDetailsBreadcrumb(queryClient, params.id)();
    } catch (e) {
      const error = e as AxiosError<{ message: string }>;
      if (error.isAxiosError && error.response?.status === 404) {
        return redirect("/procesos");
      }
      throw e;
    }
    let flow = {
      data: {
        name: "",
      },
    };
    try {
      if (!params.flowId) {
        return redirect("/procesos/" + params.id);
      }
      if (hasPermission(Permissions.flows.read)) {
        flow = await createFlowDetailsBreadcrumb(queryClient, params.flowId)();
      }
    } catch (e) {
      const error = e as AxiosError<{ message: string }>;
      if (error.isAxiosError && error.response?.status === 404) {
        return redirect("/procesos/" + params.id);
      }
      throw e;
    }
    return {
      data: {
        name: `${process?.data?.name || ""} ${FLOW_BREADCRUMB_SEPARATOR} ${flow?.data?.name || ""}`,
      },
    };
  };

const processFlowBreadcrumb = (match: {
  params?: { id?: string };
  loaderData?: { data?: { name?: string } };
}) => {
  const params = match.params;
  const [processName, flowName] = match?.loaderData?.data?.name?.split(
    FLOW_BREADCRUMB_SEPARATOR,
  ) || ["", ""];
  return [
    { label: "Procesos", path: "/procesos" },
    {
      label: processName ?? "Detalle",
      path: `/procesos/${params?.id}`,
    },
    { label: flowName ?? "Flujo" },
  ];
};

/** The flow editor and its executions view share loader, breadcrumb and guard. */
const createFlowRoute = (
  queryClient: QueryClient,
  path: string,
  element: ReactNode,
  hasPermission: (permission: string) => boolean,
) => ({
  path,
  loader: createProcessFlowLoader(queryClient, hasPermission),
  handle: { breadcrumb: processFlowBreadcrumb },
  element: (
    <RoleGuard permissions={[Permissions.flows.read, Permissions.flows.update]}>
      {element}
    </RoleGuard>
  ),
});

export const createProcesosRoutes = (
  queryClient: QueryClient,
  hasPermission: (permission: string) => boolean,
) => ({
  path: "procesos",
  children: [
    {
      index: true,
      element: (
        <RoleGuard permissions={[Permissions.processes.read]}>
          <ProcessPage />
        </RoleGuard>
      ),
      handle: makeHandle({ label: "Procesos" }),
    },
    createFlowRoute(
      queryClient,
      ":id/flujo/:flowId",
      <EditFlowPage />,
      hasPermission,
    ),
    createFlowRoute(
      queryClient,
      ":id/flujo/:flowId/ejecuciones",
      <FlowExecution />,
      hasPermission,
    ),
    {
      path: ":id",
      element: (
        <RoleGuard permissions={[Permissions.processes.read]}>
          <DetalleProceso />
        </RoleGuard>
      ),
      loader: ({ params, request }: LoaderParams & { request: Request }) => {
        const url = new URL(request.url);
        const type = url.searchParams.get("type");
        if (!params.id) {
          return redirect("/procesos");
        }
        return withNotFoundRedirect(
          () =>
            createProcessDetailsBreadcrumb(
              queryClient,
              params.id || "",
              type,
            )(),
          "/procesos",
        );
      },
      handle: {
        breadcrumb: (match: { loaderData?: { data?: { name?: string } } }) => [
          { label: "Procesos", path: "/procesos" },
          { label: match?.loaderData?.data?.name ?? "Detalle" },
        ],
      },
    },
    {
      path: "etiquetas/:id",
      element: (
        <RoleGuard permissions={[Permissions.tags.read]}>
          <TagDetailsPage />
        </RoleGuard>
      ),
      loader: async () => ({ data: { name: "Detalle de Etiqueta" } }),
      handle: {
        breadcrumb: (match: { loaderData?: { data?: { name?: string } } }) => [
          { label: "Procesos", path: "/procesos" },
          { label: "Etiquetas", path: "/procesos" },
          { label: match?.loaderData?.data?.name ?? "Detalle de Etiqueta" },
        ],
      },
    },
  ],
});
